import { useState } from "preact/hooks";
import { useRoute } from "preact-iso";
import { MAX_USER_MESSAGE_LENGTH } from "@shared/schemas";

// An EDIT reply re-emits the whole page. Measured on gemma-4 / Workers AI
// (2026-09-27): ~12,000 chars already takes ~57s of the 90s budget and
// max_tokens 8192 caps out near ~14,700, so warn before the author hits it.
const AI_EDIT_COMFORT_CHARS = 12_000;
import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  RocketLaunchIcon,
  StopIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { adminHref } from "../lib/platform-admin";
import { canManageProject } from "../lib/permissions";
import { useChatStream } from "../lib/use-chat-stream";
import { useConfirm } from "../lib/use-confirm";
import { useDeployHook } from "../lib/use-deploy-hook";
import { useDocumentDraft } from "../lib/use-document-draft";
import { useStickToBottom } from "../lib/use-stick-to-bottom";
import { ImageModal } from "../components/image-modal";
import { MarkdownPreview } from "../components/markdown-preview";
import { OwnerBanner } from "../components/owner-banner";
import { RevisionHistory } from "../components/revision-history";
import { ValidationErrors } from "../components/validation-errors";
import { EditProposalCard } from "../components/edit-proposal-card";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  PageHeader,
  SegmentedControl,
  SkeletonText,
  Textarea,
} from "../components/ui";

export function ContentChat() {
  const { params } = useRoute();
  const projectId = params.projectId!;
  const slug = params.slug!;

  const draft = useDocumentDraft({ projectId, slug });
  const stream = useChatStream({
    projectId,
    slug,
    getDraftBody: () => draft.draftBody,
    onApplyToDraft: (body) => {
      draft.setDraftBody(body);
      draft.clearSaveErrors();
    },
  });

  const { state: deployState, trigger: triggerDeploy } = useDeployHook(
    draft.project,
  );
  const canManage = canManageProject(draft.project?.viewerRole);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"source" | "preview">("preview");
  const [imagesOpen, setImagesOpen] = useState(false);

  // Keep the chat pinned to the latest reply while streaming — but only when the
  // user is already at the bottom (scrolling up to read history disables it).
  const chatScrollRef = useStickToBottom<HTMLDivElement>([
    stream.chat,
    stream.streaming,
    stream.awaitingMode,
  ]);

  const { confirm, dialog: confirmDialog } = useConfirm();

  // Save is gated on `streaming` so the user can't race an in-flight AI turn.
  async function save() {
    if (stream.streaming) return;
    await draft.save();
  }

  const aiInputDisabled = !draft.ready || stream.streaming;

  async function onDiscard() {
    const ok = await confirm({
      title: "未保存の変更を破棄しますか？",
      description: "保存済みの状態に戻ります。この操作は取り消せません。",
      confirmLabel: "破棄する",
      tone: "danger",
    });
    if (ok) draft.discardDraft();
  }

  return (
    <section class="space-y-6">
      {confirmDialog}
      <OwnerBanner project={draft.project} />

      <PageHeader
        backHref={adminHref(`/projects/${projectId}/pages`)}
        backLabel="ページ一覧"
        title={`/${slug}`}
        monoTitle
        actions={
          <>
            {draft.dirty && !stream.streaming && <Badge tone="warning">未保存</Badge>}
            <Button
              variant="secondary"
              iconLeft={<TrashIcon class="h-4 w-4" />}
              onClick={() => void onDiscard()}
              disabled={!draft.dirty || draft.saving || stream.streaming}
            >
              破棄
            </Button>
            <Button
              variant="primary"
              loading={draft.saving}
              onClick={save}
              disabled={!draft.dirty || draft.saving || stream.streaming}
            >
              {draft.saving ? "保存中…" : "保存"}
            </Button>
            <Button
              variant="secondary"
              iconLeft={<PhotoIcon class="h-4 w-4" />}
              onClick={() => setImagesOpen(true)}
              disabled={stream.streaming}
            >
              画像
            </Button>
            <Button
              variant="secondary"
              iconLeft={<ClockIcon class="h-4 w-4" />}
              onClick={() => setHistoryOpen((v) => !v)}
              disabled={stream.streaming}
            >
              履歴
            </Button>
            {canManage && (
              <Button
                variant="secondary"
                iconLeft={<RocketLaunchIcon class="h-4 w-4" />}
                loading={deployState.kind === "running"}
                onClick={triggerDeploy}
                disabled={
                  !draft.project?.deployHookConfigured ||
                  deployState.kind === "running" ||
                  stream.streaming
                }
              >
                {deployState.kind === "running" ? "デプロイ中…" : "デプロイ"}
              </Button>
            )}
          </>
        }
      />

      <ImageModal
        projectIdOrSlug={projectId}
        open={imagesOpen}
        onClose={() => setImagesOpen(false)}
      />

      {draft.loadError && (
        <Alert tone="danger">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span>本文を読み込めませんでした。{draft.loadError} 再読み込みするまで編集・保存はできません。</span>
            <Button size="sm" variant="secondary" onClick={draft.reload}>
              再読み込み
            </Button>
          </div>
        </Alert>
      )}
      <ValidationErrors errors={draft.saveErrors} variant="inline" />

      <div class="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div class="min-w-0 space-y-2">
          <div class="flex items-center justify-between">
            <SegmentedControl
              ariaLabel="ビュー切替"
              size="sm"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: "preview", label: "プレビュー" },
                { value: "source", label: "MD" },
              ]}
            />
          </div>
          {!draft.ready ? (
            <div class="rounded-lg border border-border bg-bg p-4 shadow-xs">
              <SkeletonText lines={10} />
            </div>
          ) : viewMode === "source" ? (
            <pre class="overflow-auto rounded-lg border border-border bg-bg p-4 font-mono text-xs leading-relaxed text-fg-muted shadow-xs">
              {draft.draftBody}
            </pre>
          ) : (
            <MarkdownPreview source={draft.draftBody} />
          )}
        </div>

        <Card class="flex h-[70vh] min-w-0 flex-col" elevated>
          <div class="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
            <ChatBubbleLeftRightIcon class="h-4 w-4 text-fg-subtle" />
            <span class="text-sm font-medium text-fg">AI に相談・修正を依頼</span>
          </div>
          <div
            ref={chatScrollRef}
            class="flex-1 space-y-3 overflow-y-auto px-4 py-3"
          >
            {stream.chat.length === 0 && !stream.streaming && (
              <p class="text-xs text-fg-subtle">
                テキストの追加・テーブル化・並べ替えなど、構造の変更を具体的に指示してください（例:
                「導入文の末尾に『お問い合わせはこちらまで』を追加」「価格の箇条書きを 3 列のテーブルに変換」「プロジェクト画像から内容に合う写真を探して導入文の下に追加」）。文面そのものを AI に考えさせる作文や、「どう思う？」のような相談は苦手です。修正案は
                <strong class="font-semibold text-fg">「反映する」を押すまで本文には書き込まれません。</strong>
              </p>
            )}
            {stream.chat.map((t) =>
              t.role === "user" ? (
                <div
                  key={t.id}
                  class="ml-6 whitespace-pre-wrap rounded-lg bg-bg-inset px-3 py-2 text-sm text-fg"
                >
                  {t.text}
                </div>
              ) : t.mode === "chat" ? (
                <MarkdownPreview
                  key={t.id}
                  source={t.text}
                  variant="chat"
                  streaming={t.streaming}
                />
              ) : (
                <EditProposalCard
                  key={t.id}
                  turn={t}
                  currentBody={draft.draftBody}
                  onApply={() => stream.applyEdit(t.id)}
                  onDiscard={() => stream.discardEdit(t.id)}
                />
              ),
            )}
            {stream.awaitingMode && (
              <div class="mr-6 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs text-fg-subtle">
                <span class="inline-block h-1 w-1 animate-pulse rounded-full bg-fg-faint" />
                <span class="inline-block h-1 w-1 animate-pulse rounded-full bg-fg-faint [animation-delay:120ms]" />
                <span class="inline-block h-1 w-1 animate-pulse rounded-full bg-fg-faint [animation-delay:240ms]" />
              </div>
            )}
            {stream.streamError && (
              <Alert tone="danger" class="text-xs">{stream.streamError}</Alert>
            )}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.ready) return;
              void stream.send();
            }}
            class="border-t border-border-subtle p-3"
          >
            <Textarea
              value={stream.userInput}
              maxLength={MAX_USER_MESSAGE_LENGTH}
              onInput={(e) =>
                stream.setUserInput((e.target as HTMLTextAreaElement).value)
              }
              onKeyDown={(e) => {
                // isComposing: some Japanese IMEs confirm a conversion with
                // Shift+Enter, which must not also send the message.
                if (e.key === "Enter" && e.shiftKey && !e.isComposing) {
                  e.preventDefault();
                  if (!draft.ready) return;
                  void stream.send();
                }
              }}
              disabled={aiInputDisabled}
              rows={3}
              placeholder="例: この導入文どう思う？ / 価格セクションの見出しを直して"
              // iOS Safari auto-zooms focused form controls below 16px.
              // Keep the compact 14px desktop style, but use 16px on iPhone.
              class="resize-none max-sm:text-base"
            />
            {draft.draftBody.length > AI_EDIT_COMFORT_CHARS && (
              <p class="mt-2 text-[11px] text-warning-fg">
                このページは長いため、AI の編集案が途中で切れることがあります。切れた場合は、対象のセクションを指定して依頼してください。
              </p>
            )}
            <div class="mt-2 flex items-center justify-between gap-2">
              <span class="text-[11px] text-fg-faint">
                Shift+Enter 送信 / Enter 改行
              </span>
              {stream.streaming ? (
                <Button
                  size="sm"
                  variant="secondary"
                  iconLeft={<StopIcon class="h-3.5 w-3.5" />}
                  onClick={stream.abort}
                >
                  中止
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  iconLeft={<PaperAirplaneIcon class="h-3.5 w-3.5" />}
                  disabled={!draft.ready || !stream.userInput.trim()}
                >
                  送信
                </Button>
              )}
            </div>
          </form>
        </Card>
      </div>

      {historyOpen && (
        <Card elevated>
          <CardBody class="space-y-3">
            <h3 class="text-sm font-semibold tracking-tight text-fg">
              更新履歴
            </h3>
            <RevisionHistory
              projectId={projectId}
              slug={slug}
              dirty={draft.dirty}
              refreshKey={draft.historyRefreshKey}
              onRestored={draft.applyRestoredDocument}
            />
          </CardBody>
        </Card>
      )}
    </section>
  );
}
